import { Suspense, ComponentType, useEffect } from 'react';
import { initScrollHandlers, preventJumpAndScroll } from '@/utils/scrollToHash';
import clsx from 'clsx';
import type { BlockEntry, AppProps, BlockType } from '@/types/blocks';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary.component';
import AccordionContainerBlock from '@/blocks/AccordionBlock/AccordionBlock.translation';
import AccreditationContainerBlock from '@/blocks/AccreditationBlock/AccreditationBlock.translation';
import BreadcrumbWidget from '@/blocks/BreadCrumbBlock/BreadCrumbBlock.translation';
import CookieConsentWidget from '@/blocks/CookieConsentBlock/CookieConsentBlock.translation';
import ComposableContentBlock from '@/blocks/ComposableContentBlock/ComposableContentBlock.translation';
import CtaContainerBlock from '@/blocks/CTABlock/CTABlock.translation';
import ContactInfoBlock from '@/blocks/ContactInfoBlock/ContactInfoBlock.translation';
import DonationConfirmationBlock from '@/blocks/DonationConfirmationBlock/DonationConfirmationBlock.translation';
import EpiContentArea from '@/layouts/SingleColumnLayout/SingleColumnLayout.translation';
import ErrorBlock from '@/blocks/ErrorBlock/ErrorBlock.translation';
import FeatureLinksIconsBlock from '@/blocks/FeatureLinksIconsBlock/FeatureLinksIconsBlock.translation';
import FiftyFiftyAccordionBlock from '@/blocks/FiftyFiftyAccordionBlock/FiftyFiftyAccordionBlock.translation';
import FiftyFiftyImageBlock from '@/blocks/FiftyFiftyImageBlock/FiftyFiftyImageBlock.translation';
import FiftyFiftyImageLinksBlock from '@/blocks/FiftyFiftyImageLinksBlock/FiftyFiftyImageLinksBlock.translation';
import FiftyFiftyMapBlock from '@/blocks/MapsWysiwygBlock/MapsWysiwygBlock.translation';
import FiftyFiftyVideoBlock from '@/blocks/FiftyFiftyVideoBlock/FiftyFiftyVideoBlock.translation';
import FixedContentBlock from '@/blocks/FixedContentBlock/FixedContentBlock.translation';
import FooterBlock from '@/blocks/Footer/Footer.translation';
import GlobalSearchBlock from '@/blocks/SearchResultsBlock/SearchResultsBlock.translation';
import GtmDataLayer from '@/blocks/GtmDataLayer/GtmDataLayer.component';
import NotificationBlock from '@/blocks/GlobalNotificationBlock/GlobalNotificationBlock.translation';
import HeroBannerCarouselBlock from '@/blocks/HeroBannerCarouselBlock/HeroBannerCarouselBlock.translation';
import HeroDonationBlock from '@/blocks/HeroDonationBlock/HeroDonationBlock.translation';
import HeroHeaderBlock from '@/blocks/HeroBannerBlock/HeroBannerBlock.translation';
import HeroHeaderFiftyFiftyBlock from '@/blocks/HeroHeaderFiftyFiftyBlock/HeroHeaderFiftyFiftyBlock.translation';
import HeroSearchBlock from '@/blocks/HeroSearchBlock/HeroSearchBlock.translation';
import ImageWithCaptionBlock from '@/blocks/ImageBlock/ImageBlock.translation';
import ImageCarouselBlock from '@/blocks/ImageCarouselBlock/ImageCarouselBlock.translation';
import LogoBlock from '@/blocks/LogoBlock/LogoBlock.translation';
import MegaMenuBlock from '@/blocks/GlobalHeader/GlobalHeader.translation';
import OptimizelyFormContainerBlock from '@/blocks/OptimizelyFormBlock/OptimizelyFormBlock.translation';
import QuickExit from '@/blocks/QuickExitBlock/QuickExitBlock.translation';
import QuickLinksBlock from '@/blocks/QuickLinksBlock/QuickLinksBlock.translation';
import QuoteContainerBlock from '@/blocks/PullQuoteBlock/PullQuoteBlock.translation';
import SecondaryNavigation from '@/blocks/SecondaryNavigation/SecondaryNavigation.translation';
import MetaDataBlock from '@/blocks/MetaDataBlock/MetaDataBlock.translation';
import SkipToContent from '@/blocks/SkipToContent/SkipToContent.translation';
import StatisticsTileContainerBlock from '@/blocks/StatsCardBlock/StatsCardBlock.translation';
import TabContainerBlock from '@/blocks/TabsBlock/TabsBlock.translation';
import OptimizelySubscriptionForm from '@/blocks/SubscriptionBlock/SubscriptionBlock.translation';
import SupportCta from '@/blocks/SupportCta/SupportCta.component';
import TwoColumnContentPageStructure from '@/layouts/TwoColumnLayout/TwoColumnLayout.translation';
import StoryDetailPageStructure from '@/layouts/StoriesDetailLayout/StoriesDetailLayout.translation';
import VideoBlock from '@/blocks/FeatureVideoBlock/FeatureVideoBlock.translation';
import WysiwygBlock from '@/blocks/WysiwygBlock/WysiwygBlock.translation';
import ScriptBlock from '@/blocks/ScriptBlock/ScriptBlock.translation';

export const blocks: Record<BlockType, ComponentType<any>> = {
    AccordionContainerBlock,
    AccreditationContainerBlock,
    BreadcrumbWidget,
    CookieConsentWidget,
    ComposableContentBlock,
    CtaContainerBlock,
    ContactInfoBlock,
    DonationConfirmationBlock,
    EpiContentArea,
    ErrorBlock,
    FeatureLinksIconsBlock,
    FiftyFiftyAccordionBlock,
    FiftyFiftyImageBlock,
    FiftyFiftyImageLinksBlock,
    FiftyFiftyMapBlock,
    FiftyFiftyVideoBlock,
    FixedContentBlock,
    FooterBlock,
    GlobalSearchBlock,
    GtmDataLayer,
    NotificationBlock,
    HeroBannerCarouselBlock,
    HeroDonationBlock,
    HeroSearchBlock,
    HeroHeaderBlock,
    HeroHeaderFiftyFiftyBlock,
    ImageCarouselBlock,
    ImageWithCaptionBlock,
    LogoBlock,
    MegaMenuBlock,
    OptimizelyFormContainerBlock,
    QuickLinksBlock,
    QuickExit,
    QuoteContainerBlock,
    SecondaryNavigation,
    MetaDataBlock,
    SkipToContent,
    StatisticsTileContainerBlock,
    TabContainerBlock,
    OptimizelySubscriptionForm,
    SupportCta,
    TwoColumnContentPageStructure,
    StoryDetailPageStructure,
    VideoBlock,
    WysiwygBlock,
    ScriptBlock,
};

// TODO: Fix TS warning later
const headerBlocks: Record<BlockType, ComponentType<any>> = {
    BreadcrumbWidget,
    CookieConsentWidget,
    GlobalSearchBlock,
    NotificationBlock,
    MegaMenuBlock,
    QuickExit,
    SkipToContent
};

// TODO: Fix TS warning later
const footerBlocks: Record<BlockType, ComponentType<any>> = {
    CookieConsentWidget,
    FooterBlock,
    GtmDataLayer,
    SupportCta
};

const filterBlocks = (blocks: BlockEntry[]) => {
    if (!blocks?.length) {
        return {
            headerBlockEntries: [],
            footerBlockEntries: [],
            mainBlockEntries: []
        };
    }

    const headerBlockEntries: BlockEntry[] = [];
    const footerBlockEntries: BlockEntry[] = [];
    const mainBlockEntries: BlockEntry[] = [];

    blocks.forEach((block) => {
        if (headerBlocks[block.name]) {
            headerBlockEntries.push(block);
        } else if (footerBlocks[block.name]) {
            footerBlockEntries.push(block);
        } else {
            mainBlockEntries.push(block);
        }
    });

    return { headerBlockEntries, footerBlockEntries, mainBlockEntries };
};

export const renderBlock = (block: BlockEntry, index: number) => {
    const Block = blocks[block.name];

    // Ensure the block type exists in our registry
    if (!Block) {
        // Optionally log an error or return null/placeholder
        console.log(`Block type "${block.name}" not found.`);
        return null;
    }

    return (
        // <Suspense
        //   key={`${block.name}-${index}`}
        //   fallback={<div>Loading...</div>} // Consider a more sophisticated loading state
        // >
        <ErrorBoundary
            key={`${block.name}-${index}`}
            fallback={<div>Error loading block: {block.name}</div>}
        >
            <Block {...block.props} />
        </ErrorBoundary>
        // </Suspense>
    );
};

export const App = (AppProps: AppProps) => {
    useEffect(() => {
        preventJumpAndScroll();
        initScrollHandlers();
    }, []);

    const { headerBlockEntries, footerBlockEntries, mainBlockEntries } = filterBlocks(
        AppProps.components
    )

    return (
        <div className={clsx('app')}>
            {headerBlockEntries?.map(renderBlock)}
            <main id='main'>
                {mainBlockEntries?.map(renderBlock)}
            </main>
            {footerBlockEntries?.map(renderBlock)}
        </div>
    );
};

export default App;
